<script setup lang="ts">
import { onMounted, onBeforeUnmount } from 'vue';
import dayjs from 'dayjs';
import { useAuth } from '@/stores/auth';
import { useDashboardSummary } from '@/composables/useDashboardSummary';
import { useSignalRDashboard, onDashboardInvalidated } from '@/composables/useSignalRDashboard';

import HeroGreeting from '@/components/dashboard/HeroGreeting.vue';
import OpenIssuesCard from '@/components/dashboard/OpenIssuesCard.vue';
import NewThisWeekCard from '@/components/dashboard/NewThisWeekCard.vue';
import ResolutionTimeCard from '@/components/dashboard/ResolutionTimeCard.vue';
import PendingApprovalsCard from '@/components/dashboard/PendingApprovalsCard.vue';
import CategoryBreakdown from '@/components/dashboard/CategoryBreakdown.vue';
import WeeklyTrendChart from '@/components/dashboard/WeeklyTrendChart.vue';
import RecentIssuesCard from '@/components/dashboard/RecentIssuesCard.vue';
import SourcesBreakdown from '@/components/dashboard/SourcesBreakdown.vue';
import QuickActions from '@/components/dashboard/QuickActions.vue';
import AiSummaryCard from '@/components/dashboard/AiSummaryCard.vue';

const auth = useAuth();
const { summary, isLoading, lastRefreshedAt, refresh } = useDashboardSummary();

// Register the SignalR connection lifecycle (start on first mount, stop on last unmount).
useSignalRDashboard();

// Subscribe to push invalidations so the dashboard refreshes when the server
// broadcasts summaryInvalidated. Unsubscribe on unmount.
let unsubscribeDashboard: (() => void) | null = null;
onMounted(() => {
  void refresh();
  unsubscribeDashboard = onDashboardInvalidated(() => void refresh());
});
onBeforeUnmount(() => {
  unsubscribeDashboard?.();
});

function formatRefreshedAt(date: Date | null): string {
  if (!date) return '';
  return dayjs(date).format('h:mm:ss A');
}
</script>

<template>
  <div class="flex flex-col gap-6">
    <!-- Hero greeting -->
    <HeroGreeting :name="auth.user?.displayName ?? 'Board Member'" />

    <!-- 4-column stat grid: 4-col ≥1024px, 2-col tablet, 1-col mobile -->
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      <OpenIssuesCard :stat="summary?.openIssues ?? null" :loading="isLoading" />
      <NewThisWeekCard :stat="summary?.newThisWeek ?? null" :loading="isLoading" />
      <ResolutionTimeCard :avg-days="summary?.avgResolutionDays ?? null" :loading="isLoading" />
      <PendingApprovalsCard :count="summary?.pendingApprovals ?? null" :loading="isLoading" />
    </div>

    <!-- Category + trend chart row -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <CategoryBreakdown
        :categories="summary?.byCategory ?? []"
        :loading="isLoading"
      />
      <WeeklyTrendChart
        :trend="summary?.weeklyTrend ?? []"
        :loading="isLoading"
      />
    </div>

    <!-- Recent issues (full width) -->
    <RecentIssuesCard />

    <!-- Sources + quick actions row -->
    <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SourcesBreakdown
        :sources="summary?.bySource ?? []"
        :loading="isLoading"
      />
      <QuickActions />
    </div>

    <!-- AI summary card (full width, last) -->
    <AiSummaryCard />

    <!-- Footer: last refreshed + manual refresh -->
    <div class="flex items-center justify-between text-caption text-muted pt-2 border-t border-border">
      <span v-if="lastRefreshedAt">
        Last refreshed:
        <time :datetime="lastRefreshedAt.toISOString()" class="font-mono tabular-nums">
          {{ formatRefreshedAt(lastRefreshedAt) }}
        </time>
      </span>
      <span v-else />
      <button
        class="text-caption text-muted hover:text-fg underline underline-offset-2 transition-colors"
        :disabled="isLoading"
        @click="refresh()"
      >
        {{ isLoading ? 'Refreshing…' : 'Refresh' }}
      </button>
    </div>
  </div>
</template>
